import _ from 'underscore'
import TemplateView from '../../../../TemplateView'
import TableHeader from './TableHeader'
import UserRatesRow from './UserRatesRow'
import {MAX_RATE, UserManagementConstants} from 'constants'
import {getEffectiveDate} from '../utils'
import {ShowWarning} from './constants'
import LeaveRatesMenu from '../../../../administration/userManagement/ratesEditor/LeaveRatesMenu'
import NumberFormatter from '../../../../number_formatter'
import FloatNumberValidator from '../../../../administration/userManagement/ratesEditor/FloatNumberValidator'

export class UserRatesTable extends TemplateView {
    constructor({
        readOnly,
        editMode = true,
        scrollTo = () => {},
        updateUserRates = () => {},
        deleteUserRate = () => {},
        addNewUserRate = () => {},
        rollbackLastEditedUserRateChanges = () => {},
        refreshScrollCb = () => {},
        subscribeSelectorOnScroll = () => {},
    }) {
        super()

        this.params = {
            readOnly,
            editMode,
            scrollTo,
        }

        this.actions = {
            updateUserRates,
            deleteUserRate,
            addNewUserRate,
            rollbackLastEditedUserRateChanges,
            refreshScrollCb,
            subscribeSelectorOnScroll,
        }

        this.state = {
            userManagementData: {
                defaultWorkSchedule: [],
                currentDate: (new Date()).toJSON(),
                weekStartDay: 0,
                invertedDays: {},
                decimalSeparator: null,
                activeLeaveTypes: [],
                overtimeRateCoefficient: 0,
            },

            userRates: [],
            overtimeLevel: 0,
            errors: {},

            ovrFeatureEnabled: false,
            lvtFeatureEnabled: false,

            readOnly: this.params.readOnly,
        }

        this.onAnyStatePropertyDeeplyChanged(['userManagementData', 'userRates', 'overtimeLevel', 'ovrFeatureEnabled', 'lvtFeatureEnabled', 'readOnly'],
            (state, prevState) => this._update(state, prevState)
        )

        this.onStatePropertyDeeplyChanged('errors', errors => this._updateErrors(errors))
    }

    templateHtml() {
        return '<div class="userRatesTable">' +
               '    <div class="tableHeader"/>' +
               '    <div class="userRatesList"/>' +
               '    <div class="addRecordButtonPlaceholder">' +
               '        <div class="addRecordButton">' +
               '            <div class="icon"/>' +
               '            <span class="label"><%=addRecordButtonLabel%></span>' +
               '        </div>' +
               '    </div>' +
               '    <div class="notDefinedLabel"><%-costOfWorkRatesNotDefined%></div>' +
               '    <div class="leaveRatesMenuPlaceholder"/>' +
               '</div>'
    }

    messageKeys() {
        return {
            addRecordButtonLabel: 'user.add_edit.js.rates_editor.add_rates',
            costOfWorkRatesNotDefined: 'user.edit.js.rates_editor.cost_of_work_rates_not_defined'
        }
    }

    afterRender() {
        this.tableHeader = new TableHeader().init(this.find('.tableHeader'))

        this.$userRatesList = this.find('.userRatesList')

        this.find('.addRecordButton').click(() => this.actions.addNewUserRate())

        this.$leaveRatesMenuPlaceholder = this.find('.leaveRatesMenuPlaceholder')
    }

    _updateLeaveRatesMenu(state, prevState) {
        if (this.state.lvtFeatureEnabled && state.userManagementData.activeLeaveTypes.length > 0) {
            if (!_.isEqual(state.userManagementData.activeLeaveTypes, prevState.userManagementData && prevState.userManagementData.activeLeaveTypes)) {
                this.$leaveRatesMenuPlaceholder.empty()

                this.leaveRatesMenu = new LeaveRatesMenu({
                    activeLeaveTypes: this.state.userManagementData.activeLeaveTypes,
                    numberFormatter: new NumberFormatter(window.SystemSettings.decimalSeparator, window.SystemSettings.digitGroupingSymbol, 2, false),
                    floatNumberValidator: new FloatNumberValidator(window.SystemSettings.decimalSeparator),
                    maxRate: MAX_RATE,
                    parentEl: this.$leaveRatesMenuPlaceholder[0],
                })
            }
        } else {
            this.$leaveRatesMenuPlaceholder.empty()
        }
    }

    _update(state, prevState) {
        this.$el.toggleClass('empty', this.state.userRates.length === 0)

        this.tableHeader.setState({
            showOvertime: this.state.ovrFeatureEnabled && this.state.overtimeLevel !== UserManagementConstants.OVERTIME_OFF,
            showLeave: this.state.lvtFeatureEnabled && this.state.userManagementData.activeLeaveTypes.length > 0,
        })

        this._updateLeaveRatesMenu(state, prevState)

        this.$userRatesList.empty()
        this.find('.addRecordButton').toggle(!this.state.readOnly)
        this.find('.tableHeader').toggle(this.state.userRates.length !== 0)

        if (this.state.userRates.length === 0 && this.params.editMode && this.state.readOnly) {
            this.find('.notDefinedLabel').show()
            return
        } else {
            this.find('.notDefinedLabel').hide()
        }

        this.rateRows = {}

        this.state.userRates.forEach((rate, index) => {
            const rateRow = new UserRatesRow({
                readOnly: this.state.readOnly,
                scrollTo: this.params.scrollTo,
                activeLeaveTypes: this.state.userManagementData.activeLeaveTypes,
                onChangeCb: rate => this.actions.updateUserRates({index, rate}),
                onDeleteCb: ({removePermanently}) => this.actions.deleteUserRate({rateId: rate.rateId, index, removePermanently}),
                rollbackLastEditedUserRateChanges: () => this.actions.rollbackLastEditedUserRateChanges(),
                subscribeSelectorOnScroll: this.actions.subscribeSelectorOnScroll,
                leaveRatesMenu: this.leaveRatesMenu,
            }).init(this.$userRatesList)

            rateRow.setState({
                rate,
                overtimeEnabled: this.state.ovrFeatureEnabled && this.state.overtimeLevel !== UserManagementConstants.OVERTIME_OFF,
                leavesEnabled: this.state.lvtFeatureEnabled,
                activeLeaveTypes: this.state.userManagementData.activeLeaveTypes,
                overtimeRateCoefficient: this.state.userManagementData.overtimeRateCoefficient,
                defaultWorkSchedule: this.state.userManagementData.defaultWorkSchedule,
                currentDate: this.state.userManagementData.currentDate,
                weekStartDay: this.state.userManagementData.weekStartDay,
                invertedDays: this.state.userManagementData.invertedDaysMap,
                decimalSeparator: this.state.userManagementData.decimalSeparator,
            })

            this.rateRows[index] = rateRow
        })

        this._updateErrors(this.state.errors)

        this.actions.refreshScrollCb()
    }

    _updateErrors(errors) {
        this.state.userRates.forEach((rate, index) => {
            this.rateRows[index].setState({
                error: errors[index] ? errors[index] : {}
            })
        })

        this._updateEffectiveRate()
    }

    _updateEffectiveRate() {
        const errors = this.state.errors

        const effectiveDate = getEffectiveDate(
            this.state.userRates.map(rate => rate.effectiveDate),
            this.state.userManagementData.currentDate
        )

        this.state.userRates.forEach((rate, index) => {
            this.rateRows[index].setState({
                effective: rate.effectiveDate !== null && effectiveDate === rate.effectiveDate &&
                           (!errors[index] || errors[index].showWarning !== ShowWarning.EFFECTIVE_DATE_WARNING),
            })
        })
    }
}


// WEBPACK FOOTER //
// ./web/js/panels/user/accountSettings/userRates/UserRatesTable.js